import { useCallback, useEffect, useMemo, useState } from 'react'

import { DateTime } from 'luxon'
import { FaMapMarkerAlt, FaThermometerHalf } from 'react-icons/fa'
import { FiX } from 'react-icons/fi'
import { PiWarningBold } from 'react-icons/pi'

import type { HistoryWindow } from '@/components/charts/history/useHistoryNavigation'
import { Alert } from '@/components/ui'
import DetailCard, { type DetailItem } from '@/components/ui/asset-detail/DetailCard'
import { useApi, useDashboard } from '@/hooks'
import { ShipmentUpdatePanel } from '@/pages/admin/shipments/ShipmentUpdatePanel'

import TrackerHistoryChart from './historyGraph/TrackerHistoryChart'
import { rowStatusIcon } from './TrackerTable'
import { type ApiTimestamp, TrackerHistory, TrackerShipmentRow, TrackerState } from './types'
import {
  SHIPMENT_STATE_BADGE_CLASS,
  SHIPMENT_STATE_LABELS,
  formatLux,
  formatPercent,
  formatTemp,
  formatTempRange,
  getRowStatus,
} from './utils'

const fmtTime = (iso: string | Date | null | undefined): string =>
  iso ? DateTime.fromJSDate(new Date(iso)).toFormat('yyyy-MM-dd HH:mm') : '—'

interface DefaultChart {
  from: DateTime
  to: DateTime
  periodHours: number
  /** Shipment bounds the From/To pickers start on; null for unstarted rows. */
  range: HistoryWindow | null
}

const toMs = (value: ApiTimestamp | undefined): number | null => {
  if (!value) return null
  const ms = new Date(value).getTime()
  return Number.isFinite(ms) ? ms : null
}

const na = (value: React.ReactNode): Pick<DetailItem, 'value' | 'valueClassName'> =>
  value == null || value === '—' || value === '' ? { value: '—', valueClassName: 'text-base-content/40' } : { value }

export type TrackerDetailTab = 'status' | 'graph' | 'transition' | 'plan'

/** The detail view's title: shipment or tracker, its state, the device, and the status icon. */
export const TrackerDetailHeader = ({ row, onClose }: { row: TrackerShipmentRow; onClose?: () => void }) => {
  const title = row.shipmentName ? `Shipment: ${row.shipmentName}` : `Tracker: ${row.label ?? '—'}`
  return (
    <div className="flex justify-between w-full items-center gap-4 min-w-0">
      <div className="flex items-center gap-3 min-w-0">
        <span className="truncate font-semibold">{title}</span>
        {row.shipmentState && (
          <span className={`badge badge-sm ${SHIPMENT_STATE_BADGE_CLASS[row.shipmentState]}`}>
            {SHIPMENT_STATE_LABELS[row.shipmentState]}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 text-xs shrink-0">
        {row.trackerId != null && (
          <span className="text-primary-content/80 truncate">
            {row.vendor} · SN {row.serialNumber} · IMEI {row.imei}
          </span>
        )}
        {rowStatusIcon(getRowStatus(row))}
        {onClose && (
          <button
            type="button"
            aria-label="Close details"
            className="btn btn-ghost btn-xs btn-circle"
            onClick={onClose}
          >
            <FiX className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}

interface TrackerDetailBodyProps {
  row: TrackerShipmentRow
  tracker?: TrackerState
  /** The view is showing; loading starts then and the chart resets when it closes. */
  show: boolean
  activeTab: TrackerDetailTab
  setActiveTab: (tab: TrackerDetailTab) => void
  /** In the narrow sheet: no tall minimum, cards stack until there's room for three. */
  compact?: boolean
}

/** The detail view's tabs: status, temperature graph, and the shipment's transition and plan. */
export const TrackerDetailBody = ({
  row,
  tracker,
  show,
  activeTab,
  setActiveTab,
  compact = false,
}: TrackerDetailBodyProps) => {
  const { api } = useApi()
  const units = useDashboard().configuration.units as 'Imperial' | 'Metric'

  const shipment = tracker?.shipment ?? null
  const isEnded = (row?.asOf ?? 'current') === 'shipmentEnd'
  const trackerId = row?.trackerId ?? undefined
  // Shown for any shipment, not just live ones — a completed shipment still has
  // a story worth reading; the panel itself makes clear nothing can change.
  const hasShipment = row?.shipmentId != null
  const canEditPlan = hasShipment && row.shipmentState === 'planned'

  // Chart window: a started shipment defaults to its actual bounds (start → now
  // while active, start → actual end once completed) as the pickers' range, with
  // the full span in view. Unstarted rows fall back to a trailing 24h window.
  const startMs = toMs(row?.actualStartAt)
  const endMs = toMs(row?.actualEndAt)

  const defaultChart = useMemo<DefaultChart>(() => {
    if (startMs != null) {
      const start = DateTime.fromMillis(startMs)
      const end = endMs != null ? DateTime.fromMillis(endMs) : DateTime.now()
      const span = end.diff(start, 'hours').hours
      if (span > 0) return { from: start, to: end, periodHours: span, range: { from: start, to: end } }
    }
    const from = DateTime.now().startOf('hour').minus({ hours: 24 })
    return { from, to: from.plus({ hours: 24 }), periodHours: 24, range: null }
  }, [startMs, endMs])

  const [chartData, setChartData] = useState<TrackerHistory>()
  const [chartError, setChartError] = useState('')
  const [isChartLoading, setIsChartLoading] = useState(false)
  // Remount the chart when the view closes so it reopens at the default window
  // (mirrors AssetDetailModal).
  const [chartResetKey, setChartResetKey] = useState(0)
  useEffect(() => {
    if (!show) setChartResetKey((k) => k + 1)
  }, [show])

  const fetchChartData = useCallback(
    async (from: DateTime, to: DateTime): Promise<TrackerHistory | undefined> => {
      if (!trackerId) return undefined
      const fromISO = from.toISO()
      const toISO = to.toISO()
      if (!fromISO || !toISO) return undefined
      const query = new URLSearchParams({ from: fromISO, to: toISO })
      return api.simpleGet<TrackerHistory>({
        path: `trackers/history/${trackerId}?${query.toString()}`,
        origin: 'TrackerDetail#fetchChartData',
      })
    },
    [api, trackerId]
  )

  const reloadChart = useCallback(
    async (_: typeof api, window: HistoryWindow): Promise<TrackerHistory | undefined> => {
      // Don't push the result back into chartData — the chart owns its window;
      // changing the `data` prop would reset it (see AssetDetailModal).
      try {
        return await fetchChartData(window.from, window.to)
      } catch {
        return undefined
      }
    },
    [fetchChartData]
  )

  // Initial chart load (and reload on tracker/row change).
  useEffect(() => {
    if (!show || !trackerId) return
    let cancelled = false
    setChartError('')
    setIsChartLoading(true)
    fetchChartData(defaultChart.from, defaultChart.to)
      .then((data) => {
        if (!cancelled) setChartData(data)
      })
      .catch(() => {
        if (!cancelled) setChartError('Unable to load tracker history.')
      })
      .finally(() => {
        if (!cancelled) setIsChartLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [show, trackerId, defaultChart, fetchChartData])

  const locationItems: DetailItem[] = [
    {
      label: isEnded ? 'Location at Shipment End' : 'Last Known Location',
      ...na(row.location ? `${row.location.latitude.toFixed(4)}, ${row.location.longitude.toFixed(4)}` : '—'),
    },
    ...(shipment || row.shipmentId
      ? [
          {
            label: 'Origin',
            ...na(shipment?.originName ?? row.originName ?? '—'),
          },
          ...(shipment?.originAddress ? [{ label: 'Origin Address', ...na(shipment.originAddress) }] : []),
          {
            label: 'Destination',
            ...na(shipment?.destinationName ?? row.destinationName ?? '—'),
          },
          ...(shipment?.destinationAddress
            ? [{ label: 'Destination Address', ...na(shipment.destinationAddress) }]
            : []),
          { label: 'Expected', ...na(`${fmtTime(row.expectedStartAt)} → ${fmtTime(row.expectedEndAt)}`) },
          { label: 'Actual', ...na(`${fmtTime(row.actualStartAt)} → ${fmtTime(row.actualEndAt)}`) },
          {
            label: 'Temp Range',
            ...na(formatTempRange(row.shipmentMinTemperatureCelsius, row.shipmentMaxTemperatureCelsius, units)),
          },
        ]
      : []),
  ]

  const sensorSource = tracker ?? row
  const sensorItems: DetailItem[] =
    trackerId == null
      ? [{ label: 'No tracker assigned', value: '' }]
      : [
          {
            label: 'Temperature',
            ...na(formatTemp(sensorSource.temperatureCelsius, units)),
          },
          { label: 'Humidity', ...na(formatPercent(sensorSource.humidityPercent)) },
          { label: 'Light', ...na(formatLux(sensorSource.lightLux)) },
          { label: 'Battery', ...na(formatPercent(sensorSource.batteryPercent)) },
          {
            label: isEnded ? 'As of Shipment End' : 'Last Reported',
            ...na(fmtTime(sensorSource.temperatureCelsiusTs ?? sensorSource.locationTs)),
          },
        ]

  return (
    // content-start matters: .tabs is a grid, so without it the min-height stretches the header
    // row and drops the content down the page whenever a tab's content is shorter than 550px.
    <div className={`tabs tabs-border content-start ${compact ? '' : 'min-h-[550px]'}`}>
      <input
        type="radio"
        name="tracker_tabs"
        className="tab mb-4"
        aria-label="Status"
        checked={activeTab === 'status'}
        onChange={() => setActiveTab('status')}
      />
      <div className="tab-content min-h-[400px]">
        <div className="@container">
          <div className="grid grid-cols-1 @3xl:grid-cols-3 gap-4 content-start">
            <DetailCard icon={<FaMapMarkerAlt />} title="Location" items={locationItems} />
            <DetailCard icon={<FaThermometerHalf />} title="Sensor Values" items={sensorItems} />
            <DetailCard
              icon={<PiWarningBold />}
              title="Active Alerts"
              children={<div className="text-sm text-base-content/60">No active alerts</div>}
            />
          </div>
        </div>
      </div>

      <input
        type="radio"
        name="tracker_tabs"
        className="tab mb-4"
        aria-label="Temperature Graph"
        checked={activeTab === 'graph'}
        onChange={() => setActiveTab('graph')}
        disabled={trackerId == null}
      />
      <div className="tab-content min-h-[400px]">
        {activeTab === 'graph' && (
          <div className="relative">
            {trackerId == null ? (
              <Alert type="info" message="No tracker assigned to this shipment yet." />
            ) : chartData ? (
              <>
                {chartError && <Alert type="error" message={chartError} />}
                <TrackerHistoryChart
                  key={chartResetKey}
                  data={chartData}
                  from={defaultChart.from}
                  periodHours={defaultChart.periodHours}
                  defaultRange={defaultChart.range}
                  units={units}
                  shipmentRange={
                    row.shipmentMinTemperatureCelsius != null || row.shipmentMaxTemperatureCelsius != null
                      ? { min: row.shipmentMinTemperatureCelsius, max: row.shipmentMaxTemperatureCelsius }
                      : undefined
                  }
                  reload={reloadChart}
                />
              </>
            ) : chartError ? (
              <Alert type="error" message={chartError} />
            ) : !isChartLoading ? (
              <Alert type="info" message="No history data available." />
            ) : (
              <div className="h-[300px]" />
            )}
            {isChartLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/70">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
                <p className="text-sm text-gray-600">Loading history…</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* A map row may have no shipment at all (an unassigned tracker). */}
      {hasShipment && row.shipmentId != null && (
        <>
          <input
            type="radio"
            name="tracker_tabs"
            className="tab mb-4"
            aria-label="Transition"
            checked={activeTab === 'transition'}
            onChange={() => setActiveTab('transition')}
          />
          <div className="tab-content min-h-[400px]">
            {/* No refresh callback: the context's loader resets the selected
                row, which would close this modal mid-edit. The panel shows
                its own result and the map picks it up on its 60s poll. */}
            {activeTab === 'transition' && (
              <ShipmentUpdatePanel shipmentId={row.shipmentId} companyId={row.customerId ?? null} mode="transition" />
            )}
          </div>
        </>
      )}

      {/* Re-planning only applies before departure, so the tab is offered
          only then rather than sitting there permanently disabled. */}
      {canEditPlan && row.shipmentId != null && (
        <>
          <input
            type="radio"
            name="tracker_tabs"
            className="tab mb-4"
            aria-label="Update Plan"
            checked={activeTab === 'plan'}
            onChange={() => setActiveTab('plan')}
          />
          <div className="tab-content min-h-[400px]">
            {activeTab === 'plan' && (
              <ShipmentUpdatePanel shipmentId={row.shipmentId} companyId={row.customerId ?? null} mode="plan" />
            )}
          </div>
        </>
      )}
    </div>
  )
}
